<template>
	<!-- Header -->
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<h2 class="item" v-if="viewType == ViewType.Creating">
					Новая марка автомобиля
				</h2>
				<h2 class="item" v-else>
					Карточка марки автомобиля <span v-if="originVehicleManufacturer?.name">{{ originVehicleManufacturer.name }}</span>
				</h2>
			</div>
		</el-col>	
	</el-row>

	<div class="base-form">
		<el-row>
			<el-col :span="24">	
				<el-form ref="f" v-loading="isAsyncOperationPending" :rules="rules" :model="form">
					<el-form-item label="Марка" label-position="top" prop="name">
						<el-input placeholder="Название марки автомобиля" v-model="form.name" max="255" />
					</el-form-item>

					<el-form-item label="Описание" label-position="top" prop="description">
						<el-input
							v-model="form.description"
							:autosize="{ minRows: 2, maxRows: 6 }"
							type="textarea"
							maxlength="5000"
							show-word-limit
							placeholder="Описание марки автомобиля"
						/>			
					</el-form-item>

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="16">
								<el-button type="primary" @click="onFormSubmitClick">
									Сохранить
								</el-button>	
								<el-button type="warning" @click="onFormCancelClick">
									{{ viewType == ViewType.Editing ? 'Отменить изменения' : 'Очистить форму' }} 
								</el-button>
							</el-col>
							<el-col v-if="viewType == ViewType.Editing" :span="8">
								<div style="text-align: right;">
									<el-button type="danger"
										:disabled="!isViewLoaded && isAsyncOperationPending || !isVehicleModelDeletionAllowed" 
										@click="onDeleteVehicleManufacturerClick">
										Удалить марку автомобиля
									</el-button>
								</div>
							</el-col>
						</el-row>
						<el-row v-if="viewType == ViewType.Editing" style="width: 100%;">
							<el-col :span="16">
								&nbsp;
							</el-col>
							<el-col :span="8">
								<div v-if="isViewLoaded && !isVehicleModelDeletionAllowed" style="text-align: right; line-height: 1rem; margin-top: 1rem; width: 100%;">
									<el-text type="info" size="small">
										<el-icon><Opportunity /></el-icon>
										Нельзя удалить - у марки автомобиля есть связанные модели 
									</el-text>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</el-col>		
		</el-row>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Edit, Opportunity } from '@element-plus/icons-vue';
import { ElTable, ElTag, ElLink, ElForm, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import EventHelper from '@/helpers/eventsHelper';
import ViewType from '@/models/enums/ViewType';
import store from '@/store';
import VehicleManufacturer from '@/models/VehicleManufacturer';
import VehicleManufacturersApiHelper from '@/helpers/api/VehicleManufacturersApiHelper';

interface IVehicleManufacturerForm {
	id: number;
	name: string | null;
	description: string | null;
	logo: string | null;
}

@Options({
	components: {
		CirclePlus, ElForm, ElTable, ElTag, ElLink,
		Opportunity, Edit
	},
	props: {
		params: Array
	}
})
export default class VehicleManufacturerView extends Vue {
	/** props */
	params!: Array<any>;

	/** constants **/
	editIcon = Edit;
	vehicleManufacturerApi = new VehicleManufacturersApiHelper(store.getters.jwt);
	ViewType = ViewType;
	
	/** view-models */	
	originVehicleManufacturer: VehicleManufacturer | null = null;	
	viewType: ViewType = this.ViewType.Creating;
	isViewLoaded = false;
	isVehicleModelDeletionAllowed = false;
	isAsyncOperationPending = false;

	form: IVehicleManufacturerForm = {
		id: 0,
		name: null,
		description: null,
		logo: null
	};

	rules = reactive({
		name: [
			{ required: true, validator: (a: any, b: string, c: Function) => this.vehicleManufacurerNameValidator(a, b, c), trigger: 'blur' }
		]
	});

	mounted() {			
		const vehicleManufacturer = this.params?.length && this.params[1] === true ? this.params[0] as VehicleManufacturer : undefined;
		this.initView(vehicleManufacturer?.id);
	}

	initView(vehicleManufacturerId?: number) {
		if (!vehicleManufacturerId) {
			this.viewType = ViewType.Creating;
		}
		else {
			this.viewType = ViewType.Editing;
			this.isAsyncOperationPending = true;
			this.loadVehicleManufacturer(vehicleManufacturerId)
				.then(vehicleManufacturer => {

					if (!vehicleManufacturer?.id) {
						ElNotification({
							message: 'Ошибка загрузки карточки марки автомобиля',
							type: 'error',
						});
						return;
					}

					this.originVehicleManufacturer = vehicleManufacturer;

					this.form.id = this.originVehicleManufacturer.id;
					this.form.name = this.originVehicleManufacturer.name;
					this.form.description = this.originVehicleManufacturer.description;
					this.form.logo = this.originVehicleManufacturer.logo;
					
					this.vehicleManufacturerApi
						.isDeletableAsync(this.originVehicleManufacturer.id)
						.then(isDeletable => this.isVehicleModelDeletionAllowed = isDeletable ?? false);
				})
				.finally(() => {
					this.isAsyncOperationPending = false;
					this.isViewLoaded = true;
				})
		}
	}

	onFormSubmitClick() {		
		const form = this.$refs.f as FormInstance;
		form.validate(async (valid) => {
			if (!valid) {
				return;
			}

			const vehicleRequest = {
				id: this.form.id,
				name: this.form.name,
				description: this.form.description,
				logo: this.form.logo
			};
	
			const isCreatingModeOn = this.viewType == ViewType.Creating;
			const vehicleAsyncOperationHandler = isCreatingModeOn ? this.createVehicleManufacturer : this.updateVehicleManufacturer;
			
			try {		
				this.isAsyncOperationPending = true;
				const response = await vehicleAsyncOperationHandler(vehicleRequest);

				if (response != null) {
					EventHelper.dispatchEvent('vehicleManufacturersListUpdate');
					ElNotification({
						message: `Модель автомобиля ${this.form.name} успешно ${isCreatingModeOn ? 'создана' : 'изменена'}`,
						type: 'success',
					});

					if (isCreatingModeOn) {
						this.initView(response.id);
					} else {
						this.initView(this.originVehicleManufacturer!.id);
					}
				} else {
					throw new Error();
				}
			}
			catch {
				ElNotification({
					message: `Ошибка ${(isCreatingModeOn ? 'создания' : 'изменения')} марки автомобиля ${this.form.name}`,
					type: 'error',
				});
			} finally {
				this.isAsyncOperationPending = false;
			}
		});
	}

	onFormCancelClick() {
		ElMessageBox.confirm(
				'Все несохраненные данные буду потеряны. Продолжить ?',
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'warning',
					confirmButtonClass: 'messagebox-danger-button'
				}
			)
			.then(() => {				
				if (this.viewType == ViewType.Creating) {
					this.form.id = 0;
					this.form.name = null;
					this.form.description = null;						
				} else {
					this.form.name = this.originVehicleManufacturer!.name;
					this.form.description = this.originVehicleManufacturer!.description;
					this.form.logo = this.originVehicleManufacturer!.logo;
				}
			});
	}

	onDeleteVehicleManufacturerClick() {
		ElMessageBox.confirm(
			`Марка автомобиля ${this.originVehicleManufacturer?.name} будет удалена. Продолжить ?`, {
			confirmButtonText: 'OK',
			cancelButtonText: 'Отмена',
			type: 'warning',
		})
		.then(async () => {
			this.isAsyncOperationPending = true;
			const isDeleted = await this.deleteVehicleManufacturer(this.originVehicleManufacturer!);
			if (isDeleted) {
				this.closeCurrentTab();
				ElNotification({				
					message: `Марка автомобиля ${this.originVehicleManufacturer!.name} удалена`,
					type: 'success',
				});
			} else {
				ElNotification({				
					message: `Ошибка удаления марки автомобиля ${this.originVehicleManufacturer!.name}`,
					type: 'error',
				});
			}
			this.isAsyncOperationPending = false;
		});
	}

	createVehicleManufacturer(request: any) : Promise<VehicleManufacturer | null> {
		return this.vehicleManufacturerApi.createAsync(request);
	}

	loadVehicleManufacturer(id: number) : Promise<VehicleManufacturer | null> {
		return this.vehicleManufacturerApi.getByIdAsync(id);
	}

	updateVehicleManufacturer(request: any) : Promise<VehicleManufacturer | null> {
		return this.vehicleManufacturerApi.updateAsync(request);
	}

	deleteVehicleManufacturer(vehicleManufacturer: VehicleManufacturer) : Promise<boolean> {		
		return this.vehicleManufacturerApi.deleteAsync(vehicleManufacturer.id);
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}
	
	async vehicleManufacurerNameValidator(_: any, modelName: string, callback: Function) {
		if (!modelName) {
			callback(new Error('Название марки автомобиля должно быть указано'));
			return false;
		}

		try {
			let vehicleExists;
			if (this.viewType == ViewType.Creating) {
				vehicleExists = await this.vehicleManufacturerApi.isAlreadyExistAsync(this.form.name!);
			} else {
				vehicleExists = await this.vehicleManufacturerApi.isAlreadyExistAsync(this.form.name!, this.form.id);
			}
			
			if (vehicleExists) {
				callback(new Error('Такая марка автомобиля уже существует'));
				return false;
			} 
			return true;			
		} catch {
			return true;
		}
	}	
}
</script>

<style scoped>
.messagebox-danger-button {
	border-color: var(--el-color-error);
	background: var(--el-color-error-light-3);
}

	.messagebox-danger-button:hover {
		border-color: var(--el-color-error);
		background: var(--el-color-danger);
	}
</style>